/**
 * A draggable divider between two panes (DESIGN.md §20).
 *
 * Owns the interaction, never the value: the page keeps the width (and its
 * persistence), this reports where the divider was dragged to. `onResize` runs
 * on every pointer move, `onCommit` once at the end — so a preference is
 * written to storage once per drag, not sixty times a second.
 *
 * A 1px line with a 6px hit area: thin enough to read as chrome, wide enough
 * to grab. Keyboard users get the same control (`role="separator"` + arrows),
 * which is why it is a focusable element rather than a bare `<div>` with a
 * cursor.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';

interface PaneResizeHandleProps {
  /** Current width of the resizable pane, in px. */
  width: number;
  min: number;
  max: number;
  /** Which side of the divider the resizable pane sits on. */
  side: 'left' | 'right';
  /** Live, during a drag. */
  onResize: (next: number) => void;
  /** Final value — persist here. */
  onCommit: (next: number) => void;
  /** Double-click: back to the default width. */
  onReset: () => void;
  ariaLabel: string;
  /** Id of the pane this divider sizes. */
  controlsId?: string;
  /** Keyboard nudge, in px. */
  step?: number;
  className?: string;
}

const DEFAULT_STEP = 16;

export function PaneResizeHandle({
  width,
  min,
  max,
  side,
  onResize,
  onCommit,
  onReset,
  ariaLabel,
  controlsId,
  step = DEFAULT_STEP,
  className,
}: PaneResizeHandleProps) {
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  // Pointer events can fire faster than the screen refreshes; resizing is a
  // layout change, so coalesce to one update per frame.
  const frame = useRef<number | null>(null);
  const pendingX = useRef<number | null>(null);

  const cancelFrame = () => {
    if (frame.current != null) window.cancelAnimationFrame(frame.current);
    frame.current = null;
    pendingX.current = null;
  };

  /**
   * The pointer leaves the 6px handle within a frame of the drag starting, so
   * the cursor and the selection lock have to sit on the document or the
   * cursor flickers back to a text caret and the drag selects the panes.
   *
   * Applied HERE rather than in an effect keyed on `dragging`, and without
   * `preventDefault()` on pointerdown: cancelling pointerdown also cancels the
   * click that focuses the handle, and a splitter you cannot tab-and-focus by
   * clicking is a worse trade than one frame of selection risk.
   */
  const bodyStyle = useRef<{ cursor: string; userSelect: string } | null>(null);

  const lockDocument = () => {
    if (bodyStyle.current) return;
    const { style } = document.body;
    bodyStyle.current = { cursor: style.cursor, userSelect: style.userSelect };
    style.cursor = 'col-resize';
    style.userSelect = 'none';
  };

  const releaseDocument = () => {
    const previous = bodyStyle.current;
    if (!previous) return;
    bodyStyle.current = null;
    document.body.style.cursor = previous.cursor;
    document.body.style.userSelect = previous.userSelect;
  };

  // Unmounting mid-drag (the pane closes under the pointer) must not leave the
  // whole document stuck on a resize cursor.
  useEffect(
    () => () => {
      cancelFrame();
      releaseDocument();
    },
    [],
  );

  const clamp = (value: number) => Math.min(max, Math.max(min, Math.round(value)));

  const widthFor = (clientX: number) => {
    const current = drag.current;
    if (!current) return width;
    const delta = clientX - current.startX;
    return clamp(current.startWidth + (side === 'right' ? -delta : delta));
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width };
    lockDocument();
    setDragging(true);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    pendingX.current = event.clientX;
    if (frame.current != null) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      const clientX = pendingX.current;
      if (clientX != null && drag.current) onResize(widthFor(clientX));
    });
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    const next = widthFor(event.clientX);
    cancelFrame();
    drag.current = null;
    releaseDocument();
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onCommit(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Left always moves the divider left, whichever pane is being sized.
    const towardsLeft = event.key === 'ArrowLeft';
    const towardsRight = event.key === 'ArrowRight';
    if (!towardsLeft && !towardsRight) return;
    event.preventDefault();
    const grow = side === 'right' ? towardsLeft : towardsRight;
    onCommit(clamp(width + (grow ? step : -step)));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      {...(controlsId ? { 'aria-controls': controlsId } : {})}
      tabIndex={0}
      data-resizing={dragging ? '' : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative w-1.5 shrink-0 cursor-col-resize touch-none self-stretch',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border',
          'transition-[background-color,width] duration-200 motion-reduce:transition-none',
          'group-hover:w-0.5 group-hover:bg-threadheads-purple/60',
          'group-focus-visible:w-0.5 group-focus-visible:bg-threadheads-purple',
          'group-data-[resizing]:w-0.5 group-data-[resizing]:bg-threadheads-purple',
        )}
      />
    </div>
  );
}
